/**
 * « Actifs de notoriété » — READ-ONLY view of the brand's own Cibles `contenu` targets,
 * one tab per own `marque` target (Brand Knowledge page).
 *
 * Reads only. It never bootstraps a marque the way `useTargetsData` does on an empty
 * partition: a page view here must not be a write. Editing happens in Cibles
 * (`/geo-strategy?cibles=contenu#cibles`).
 *
 * Pagination follows `nextCursor` behind an explicit « Charger plus » and states how many
 * rows are shown out of the manifest count, so a long list is never silently truncated.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { useTranslation } from 'react-i18next';
import type { TargetsManifest } from '@traaker/shared';
import { api } from '../../../services/api';
import DomainFavicon from '../../competitors/DomainFavicon';
import { apexDomain, extractDomain, getUrlPath } from '../../../pages/targets/CatalogueViews';
import type { TargetRow } from '../../../pages/targets/CATEGORIES';
import { targetColorHex } from '../../../utils/targetColors';

const CIBLES_HREF = '/geo-strategy?cibles=contenu#cibles';
const PAGE = 200;
/** Tab key for contents attached to no marque. Matches the manifest's counter key. */
const UNPARENTED = '__root__';

type Page = { items: TargetRow[]; nextCursor: string | null };
type TabData = { rows: TargetRow[]; cursor: string | null; loading: boolean; error: string | null; loaded: boolean };

const EMPTY_TAB: TabData = { rows: [], cursor: null, loading: false, error: null, loaded: false };

const field = (row: TargetRow, name: string): string | undefined => {
  const v = row.fields.find((f) => f.name === name)?.value;
  return typeof v === 'string' && v.trim() ? v : undefined;
};

export default function KnowledgeAssetsView({ brandId }: { brandId: string }) {
  const { t, i18n } = useTranslation();
  const [marques, setMarques] = useState<TargetRow[] | null>(null);
  const [manifest, setManifest] = useState<TargetsManifest | null>(null);
  const [marquesError, setMarquesError] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [tabs, setTabs] = useState<Record<string, TabData>>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // Own marques (every page, never truncated) + the manifest counters, in parallel.
  useEffect(() => {
    let cancelled = false;
    setMarques(null);
    setManifest(null);
    setMarquesError(null);
    setTabs({});
    setActive(null);
    (async () => {
      try {
        const all: TargetRow[] = [];
        let cursor: string | null = null;
        const [summary] = await Promise.all([
          api.get<TargetsManifest | null>(`/brands/${brandId}/targets/summary`).catch(() => null),
          (async () => {
            do {
              const params = new URLSearchParams({ category: 'marque', ownerId: 'own', limit: '100' });
              if (cursor) params.set('cursor', cursor);
              const page: Page = await api.get<Page>(`/brands/${brandId}/targets?${params}`);
              all.push(...page.items.filter((r) => (r.ownerId || 'own') === 'own'));
              cursor = page.nextCursor;
            } while (cursor && !cancelled);
          })(),
        ]);
        if (cancelled) return;
        // Root marque(s) first, then sub-marques, each alphabetically.
        all.sort((a, b) =>
          (a.parentTargetId ? 1 : 0) - (b.parentTargetId ? 1 : 0) || a.label.localeCompare(b.label),
        );
        setManifest(summary);
        setMarques(all);
        setActive(all[0]?.targetId ?? (unparentedCount(summary) > 0 ? UNPARENTED : null));
      } catch (e: any) {
        if (!cancelled) {
          setMarquesError(e?.message ?? t('brandStrategy.assets.loadError'));
          setMarques([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandId, t]);

  const counts = manifest?.countsByParentAndCategory ?? {};
  const countOf = (key: string): number | undefined =>
    manifest ? ((counts[key] as Record<string, number> | undefined)?.contenu ?? 0) : undefined;
  const showUnparented = unparentedCount(manifest) > 0;

  const loadMore = useCallback(
    async (key: string, from: string | null) => {
      setTabs((prev) => ({ ...prev, [key]: { ...(prev[key] ?? EMPTY_TAB), loading: true, error: null } }));
      try {
        const params = new URLSearchParams({ category: 'contenu', ownerId: 'own', limit: String(PAGE) });
        if (key !== UNPARENTED) params.set('parentTargetId', key);
        if (from) params.set('cursor', from);
        const page = await api.get<Page>(`/brands/${brandId}/targets?${params}`);
        // The API has no "no parent" filter: the unattached tab filters the own partition here,
        // and keeps offering « Charger plus » while the cursor runs, so nothing is hidden.
        const items = key === UNPARENTED ? page.items.filter((r) => !r.parentTargetId) : page.items;
        setTabs((prev) => {
          const cur = prev[key] ?? EMPTY_TAB;
          return {
            ...prev,
            [key]: { rows: [...cur.rows, ...items], cursor: page.nextCursor, loading: false, error: null, loaded: true },
          };
        });
      } catch (e: any) {
        setTabs((prev) => ({
          ...prev,
          [key]: { ...(prev[key] ?? EMPTY_TAB), loading: false, loaded: true, error: e?.message ?? t('brandStrategy.assets.loadError') },
        }));
      }
    },
    [brandId, t],
  );

  useEffect(() => {
    if (active && !tabs[active]) loadMore(active, null);
  }, [active, tabs, loadMore]);

  const tab = active ? tabs[active] ?? EMPTY_TAB : EMPTY_TAB;

  const groups = useMemo(() => {
    const map = new Map<string, TargetRow[]>();
    for (const row of tab.rows) {
      const url = field(row, 'url') ?? row.label ?? '';
      const apex = apexDomain(extractDomain(url));
      if (!map.has(apex)) map.set(apex, []);
      map.get(apex)!.push(row);
    }
    for (const rows of map.values()) {
      rows.sort((a, b) => (field(b, 'publishedAt') ?? '').localeCompare(field(a, 'publishedAt') ?? ''));
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [tab.rows]);

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' }),
    [i18n.language],
  );
  const fmtDate = (iso?: string) => {
    if (!iso) return undefined;
    const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? iso : dateFmt.format(d);
  };

  const cta = (
    <Button
      component={RouterLink}
      to={CIBLES_HREF}
      size="small"
      variant="outlined"
      endIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
      sx={{ borderColor: 'divider', color: 'text.primary', flexShrink: 0, '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
    >
      {t('brandStrategy.assets.manage')}
    </Button>
  );

  if (marques === null) {
    return (
      <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (marques.length === 0 && !showUnparented) {
    return (
      <EmptyState
        title={marquesError ? t('brandStrategy.assets.loadError') : t('brandStrategy.assets.emptyTitle')}
        body={marquesError ?? t('brandStrategy.assets.emptyNoMarque')}
        action={cta}
      />
    );
  }

  const tabKeys = [...marques.map((m) => m.targetId), ...(showUnparented ? [UNPARENTED] : [])];
  const total = active ? countOf(active) : undefined;

  return (
    <Box>
      {/* Marque switcher + CTA */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.25, bgcolor: '#F3F3F6', borderRadius: '10px', p: '2px' }}>
          {tabKeys.map((key) => {
            const m = marques.find((x) => x.targetId === key);
            const on = key === active;
            const n = countOf(key);
            return (
              <Box
                key={key}
                component="button"
                type="button"
                onClick={() => setActive(key)}
                sx={{
                  border: 0, font: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.75,
                  px: 1.5, py: 0.625, borderRadius: '8px', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap',
                  color: on ? 'text.primary' : 'text.secondary',
                  bgcolor: on ? 'background.paper' : 'transparent',
                  boxShadow: on ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {m ? (
                  <Box component="i" sx={{ width: 8, height: 8, borderRadius: 999, bgcolor: targetColorHex(m.color), display: 'block' }} />
                ) : (
                  <Box component="i" sx={{ width: 8, height: 8, borderRadius: 999, border: '1.5px dashed', borderColor: 'text.disabled', display: 'block' }} />
                )}
                {m ? m.label : t('brandStrategy.assets.unparented')}
                {n !== undefined && (
                  <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                    {n}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ flex: 1 }} />
        {cta}
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', bgcolor: 'background.paper', overflow: 'hidden' }}>
        {/* Meta strip */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, bgcolor: '#FAFBFD',
            borderBottom: '1px solid', borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary' }}>
            {t('brandStrategy.assets.summary', { count: tab.rows.length, domains: groups.length })}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{t('brandStrategy.assets.readOnly')}</Typography>
        </Box>

        {tab.error && (
          <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: 'error.main' }}>{tab.error}</Typography>
        )}

        {tab.loaded && tab.rows.length === 0 && !tab.cursor && !tab.error && (
          <EmptyState
            flat
            title={t('brandStrategy.assets.emptyTitle')}
            body={t('brandStrategy.assets.emptyTab')}
            action={cta}
          />
        )}

        {groups.map(([apex, rows]) => {
          const isCollapsed = collapsed.has(apex);
          return (
            <Box key={apex}>
              <Box
                component="button"
                type="button"
                onClick={() =>
                  setCollapsed((prev) => {
                    const next = new Set(prev);
                    if (next.has(apex)) next.delete(apex);
                    else next.add(apex);
                    return next;
                  })
                }
                sx={{
                  width: '100%', border: 0, font: 'inherit', cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 0.875,
                  bgcolor: alpha('#F4F4F6', 0.9), borderBottom: '1px solid', borderColor: 'divider',
                  position: 'sticky', top: 0, zIndex: 1,
                  '&:hover': { bgcolor: '#EFEFF3' },
                }}
              >
                <DomainFavicon domain={apex} name={apex} size={18} />
                <Typography sx={{ fontFamily: '"Inter Tight", Inter, sans-serif', fontSize: 13, fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {apex}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  {rows.length}
                </Typography>
                <Box component="span" sx={{ fontSize: 10, color: 'text.disabled', transform: isCollapsed ? 'none' : 'rotate(90deg)', transition: 'transform .15s' }}>
                  ▶
                </Box>
              </Box>
              {!isCollapsed &&
                rows.map((row) => {
                  const url = field(row, 'url') ?? row.label ?? '';
                  const host = extractDomain(url);
                  const path = getUrlPath(url, host) || '/';
                  const sub = host !== apex ? host.slice(0, Math.max(0, host.length - apex.length - 1)) : '';
                  const title = field(row, 'title') ?? (row.label && row.label !== url ? row.label : undefined);
                  const contentType = field(row, 'contentType');
                  const date = fmtDate(field(row, 'publishedAt'));
                  return (
                    <Box
                      key={row.targetId}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pl: 5.25, py: 0.875, minHeight: 44,
                        borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: '#FCFBFF' },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {title && (
                          <Typography sx={{ fontSize: 13.5, fontWeight: 500, letterSpacing: '-.008em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {title}
                          </Typography>
                        )}
                        <Typography
                          component="a"
                          href={url.startsWith('http') ? url : `https://${url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: 'block', fontSize: title ? 11.5 : 13, fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                            color: title ? 'text.disabled' : 'text.secondary', textDecoration: 'none',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                          }}
                        >
                          {sub ? `${sub} · ${path}` : path}
                        </Typography>
                      </Box>
                      {contentType && (
                        <Box
                          component="span"
                          sx={{ flexShrink: 0, fontSize: 11, fontWeight: 500, lineHeight: '16px', px: 0.75, py: '1px', borderRadius: '5px', bgcolor: '#F4F4F5', color: 'text.secondary', whiteSpace: 'nowrap' }}
                        >
                          {t(`cibles.contentType.${contentType}`)}
                        </Box>
                      )}
                      <Typography sx={{ flex: '0 0 92px', textAlign: 'right', fontSize: 12, color: date ? 'text.secondary' : 'text.disabled', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {date ?? '—'}
                      </Typography>
                    </Box>
                  );
                })}
            </Box>
          );
        })}

        {(tab.loading || tab.cursor) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, bgcolor: '#FAFBFD' }}>
            {tab.loading ? (
              <CircularProgress size={16} />
            ) : (
              <Button size="small" variant="outlined" onClick={() => active && loadMore(active, tab.cursor)} sx={{ borderColor: 'divider', color: 'text.primary' }}>
                {t('brandStrategy.assets.loadMore')}
              </Button>
            )}
            {total !== undefined && active !== UNPARENTED && (
              <Typography sx={{ fontSize: 12, color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                {t('brandStrategy.assets.shownOf', { shown: tab.rows.length, total })}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function unparentedCount(manifest: TargetsManifest | null): number {
  const c = manifest?.countsByParentAndCategory?.[UNPARENTED] as Record<string, number> | undefined;
  return c?.contenu ?? 0;
}

function EmptyState({ title, body, action, flat }: { title: string; body: string; action: React.ReactNode; flat?: boolean }) {
  return (
    <Box
      sx={{
        py: 6, px: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
        ...(flat ? {} : { border: '1px dashed', borderColor: 'divider', borderRadius: '12px', bgcolor: 'background.paper' }),
      }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'action.hover', color: 'primary.main', mb: 0.5 }}>
        <ArticleOutlinedIcon fontSize="small" />
      </Box>
      <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', maxWidth: 420, lineHeight: 1.5, mb: 1 }}>{body}</Typography>
      {action}
    </Box>
  );
}
